import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { RJSFSchema } from '@rjsf/utils';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';

import preparePropertyFormSchema from '~/components/Vendors/Properties/helpers/preparePropertyFormSchema';

import { useGetAmenities } from '~/hooks/apis/accommodation/useGetAmenities';
import { useGetPropertiesSchema } from '~/hooks/apis/accommodation/useGetProperty';
import { useRoomTypesSchema } from '~/hooks/apis/accommodation/useRoomTypesSchema';
import { useSaveOfferBuilderRoom } from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';
import {
  useCrmResyncOpportunity,
  useCrmResyncProgressPercent,
  useGetCrmResyncStatus,
} from '~/hooks/apis/offers/useCrmResyncOpportunity';
import { useGenerateBulkBeddingConfigurations } from '~/hooks/apis/offers/useGenerateBeddingConfiguration';
import { useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';
import type { OpportunityRelatedRecords } from '~/hooks/apis/offers/useGetOpportunityRelated';
import { useCreateBatchRoomRates } from '~/hooks/apis/reservation/useCreateBatchRoomRates';
import { useCreateRoomTypeCapacity } from '~/hooks/apis/reservation/useCreateRoomTypeCapacity';
import { useGetRatePlanCreateSchema } from '~/hooks/apis/reservation/usePropertyRatePlans';

import OffersService from '~/services/OffersService';
import PackagesService from '~/services/PackagesService';
import ReservationService from '~/services/ReservationService';

import { runInChunks } from '~/utils/deleteAllInChunks';
import getErrorMessage from '~/utils/getErrorMessage';
import { mapSFCancellationPolicyToAdmin } from '~/utils/mapSFCancelltionPolicyToAdmin';
import { reportError } from '~/utils/reportError';
import { isSalesforceOpportunityId } from '~/utils/validateSalesforceId';

import CapacityStep from './CapacityStep';
import type { CapacityRoomRate } from './CapacityStep/utils';
import { DEFAULT_CAPACITY_COMBINATIONS, getDistinctAppliedRoomRates } from './CapacityStep/utils';
import ExitConfirmationDialog from './ExitConfirmationDialog';
import PackageSetupStep, { type PackageSetupStepHandle } from './PackageSetupStep';
import PrerequisitesStep from './PrerequisitesStep';
import type { PrerequisiteItem, PrerequisiteStatus } from './PrerequisitesStep';
import PropertyStep from './PropertyStep';
import RatePlanStep from './RatePlanStep';
import {
  FLASH_RATE_PLAN_PRODUCT_TYPE,
  buildCreatedRatePlansFromDrafts,
  buildDefaultRatePlanDraft,
  getDefaultFlashRatePlanName,
  getRatePlanDraftsValidationError,
  isCreatedRatePlanDraft,
} from './RatePlanStep/utils';
import type { FlashRatePlanDraft } from './RatePlanStep/utils';
import RoomRatesStep from './RoomRatesStep';
import { buildRoomRateRooms } from './RoomRatesStep/utils';
import RoomsStep from './RoomsStep';
import {
  buildBlankRoomDraft,
  buildRoomCreatePayload,
  getCreatableRoomDrafts,
  getPropertyRoomTypes,
  getSalesforceRoomsMissingExistingRoomTypes,
  mapGeneratedBeddingConfigurationToBedGroups,
  mapSalesforceRoomsToRoomDrafts,
} from './RoomsStep/utils';
import type { FlashOfferRoomDraft } from './RoomsStep/utils';
import TaxesFeesStep from './TaxesFeesStep';
import UnmatchedPackageRoomPicker from './UnmatchedPackageRoomPicker';
import WizardActions from './WizardActions';
import {
  CREATE_NEW_PROPERTY,
  DEFAULT_NEW_PROPERTY_LOGO_ID,
  PACKAGE_SETUP_FORM_ID,
  STEPS,
  TAXES_FEES_FORM_ID,
} from './constants';
import { resolveRoomNameMatchesWithAi } from './llmRoomNameMatcher';
import type { AppliedFlashRoomRate, FlashOfferProperty, SetUpFlashOfferSectionProps } from './types';
import {
  buildFlashPackageRoomRateAssignments,
  getFlashRatePlanNameFromTravelStart,
  getMissingRequiredFieldLabels,
  getMissingRequiredPropertyFields,
  getNextButtonLabel,
  getOfferPackageName,
  getPackagePersistId,
  getPackageUpdateErrorDetail,
  getSaveStepButtonLabel,
  mapExtraGuestSurchargeAgeRangesToPropertyFormData,
  mapRedemptionOptionsToPropertyFormData,
  mapVendorBillingToPropertyFormData,
} from './utils';
import type { UnmatchedFlashPackage } from './utils';

function hasValidDealOptions(deals?: Array<unknown>): boolean {
  return !!deals?.length;
}

export default function SetUpFlashOfferSection({
  disabled,
  onPropertiesRefresh,
  properties = [],
  vendor,
  vendorId,
}: SetUpFlashOfferSectionProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTriggeredSalesforceOfferId = searchParams.get('setupFlashOffer') ?? '';
  const defaultPropertyId = properties.find((property) => !property.deleted_at)?.id ?? CREATE_NEW_PROPERTY;
  const [salesforceOfferId, setSalesforceOfferId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [propertyWarningMessage, setPropertyWarningMessage] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [prerequisiteCheckStatus, setPrerequisiteCheckStatus] = useState<
    'idle' | 'checking' | 'ready' | 'not-ready' | 'not-found' | 'wrong-vendor' | 'error'
  >('idle');
  const [validatedOpportunity, setValidatedOpportunity] = useState<App.OpportunityPage | undefined>(undefined);
  const [preloadedOpportunityRelated, setPreloadedOpportunityRelated] = useState<OpportunityRelatedRecords | undefined>(
    undefined,
  );
  const [isExitConfirmationOpen, setIsExitConfirmationOpen] = useState(false);
  const [isCreatingNewProperty, setIsCreatingNewProperty] = useState(false);
  const [isCreatingRatePlan, setIsCreatingRatePlan] = useState(false);
  const [isCreatingRooms, setIsCreatingRooms] = useState(false);
  const [isCreatingRoomRates, setIsCreatingRoomRates] = useState(false);
  const [hasAppliedRoomRates, setHasAppliedRoomRates] = useState(false);
  const [isApplyingDefaultCapacity, setIsApplyingDefaultCapacity] = useState(false);
  const [capacityAppliedRoomRateIds, setCapacityAppliedRoomRateIds] = useState<Array<string>>([]);
  const [capacityFailedRoomRateIds, setCapacityFailedRoomRateIds] = useState<Array<string>>([]);
  const [isValidatingOpportunity, setIsValidatingOpportunity] = useState(false);
  const [resyncRequestedAt, setResyncRequestedAt] = useState<string | undefined>(undefined);
  const [isAdvancingStep, setIsAdvancingStep] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPropertyId, setSelectedPropertyId] = useState(defaultPropertyId);
  const [createdProperty, setCreatedProperty] = useState<FlashOfferProperty | null>(null);
  const [createdRatePlans, setCreatedRatePlans] = useState<Array<App.RatePlan>>([]);
  const [ratePlanDrafts, setRatePlanDrafts] = useState<Array<FlashRatePlanDraft>>([]);
  const [expandedRatePlanDraftIds, setExpandedRatePlanDraftIds] = useState<Array<string>>([]);
  const [additionalAmenityOptions, setAdditionalAmenityOptions] = useState<Array<string>>([]);
  const [salesforceRooms, setSalesforceRooms] = useState<Array<SalesforceRoom>>([]);
  const [propertyFormData, setPropertyFormData] = useState<Record<string, unknown>>({});
  const [roomDrafts, setRoomDrafts] = useState<Array<FlashOfferRoomDraft>>([]);
  const [unmatchedSalesforceRoomNames, setUnmatchedSalesforceRoomNames] = useState<Array<string>>([]);
  const [aiRoomNameOverrides, setAiRoomNameOverrides] = useState<Record<string, string>>({});
  const [appliedFlashRoomRates, setAppliedFlashRoomRates] = useState<Array<AppliedFlashRoomRate>>([]);
  const [selectedRoomRatePlanIds, setSelectedRoomRatePlanIds] = useState<Record<string, Array<string>>>({});
  const [isSavingTaxesFees, setIsSavingTaxesFees] = useState(false);
  const [isSavingPackageSetup, setIsSavingPackageSetup] = useState(false);
  const [matchedPackages, setMatchedPackages] = useState<Array<App.Package>>([]);
  const [isLinkingPackages, setIsLinkingPackages] = useState(false);
  const [hasAttemptedPackageLink, setHasAttemptedPackageLink] = useState(false);
  const [matchedRoomRateCount, setMatchedRoomRateCount] = useState(0);
  const [packageLinkFailures, setPackageLinkFailures] = useState<Array<string>>([]);
  const [unmatchedPackages, setUnmatchedPackages] = useState<Array<UnmatchedFlashPackage>>([]);
  const [assignableRoomTypes, setAssignableRoomTypes] = useState<Array<Pick<App.RoomType, 'id' | 'name'>>>([]);
  const [manualPackageRoomTypeIds, setManualPackageRoomTypeIds] = useState<Record<string, string>>({});
  const [hasUnresolvedFlexibleNightsWarning, setHasUnresolvedFlexibleNightsWarning] = useState(false);
  const packageSetupStepRef = useRef<PackageSetupStepHandle>(null);
  const validationRunId = useRef(0);
  const prerequisiteCheckStartedForRef = useRef<string | undefined>(undefined);
  const manualRoomDraftCounter = useRef(0);
  const ratePlanDraftCounter = useRef(0);
  const defaultRoomRateSelectionKey = useRef<string | null>(null);
  const defaultRatePlanDraftValues = useRef({
    ratePlanCancellationPolicy: '',
    ratePlanName: getDefaultFlashRatePlanName(),
  });
  const trimmedSalesforceOfferId = salesforceOfferId.trim();
  const contentPageUrl = trimmedSalesforceOfferId
    ? `/offers/${trimmedSalesforceOfferId}/offer-builder/cm-checklist`
    : '';
  const isCreatingProperty = selectedPropertyId === CREATE_NEW_PROPERTY;
  const isPrerequisitesStep = activeStep === 1;
  const isPropertyStep = activeStep === 2;
  const isRoomStep = activeStep === 3;
  const isRatePlanStep = activeStep === 4;
  const isRoomRatesStep = activeStep === 5;
  const isCapacityStep = activeStep === 6;
  const isTaxesAndFeesStep = activeStep === 7;
  const isPackageSetupStep = activeStep === 8;
  const isContentStep = activeStep === STEPS.length - 1;
  const isCreatePropertyStep = isPropertyStep && isCreatingProperty;
  const { refetch: refetchOpportunity } = useGetOpportunity(trimmedSalesforceOfferId);
  const { refetch: refetchOpportunityRelated } = useGetOpportunityRelated(trimmedSalesforceOfferId, { enabled: false });
  const crmResyncMutation = useCrmResyncOpportunity();
  const { data: resyncStatus, isError: isResyncStatusError } = useGetCrmResyncStatus(
    trimmedSalesforceOfferId,
    resyncRequestedAt,
    { enabled: !!resyncRequestedAt },
  );
  const hasResyncFailed = isResyncStatusError || resyncStatus?.status === 'failed';
  const isResyncComplete = resyncStatus?.status === 'complete';
  const isWaitingForResync = isPrerequisitesStep && !isResyncComplete && !hasResyncFailed;
  const resyncProgressPercent = useCrmResyncProgressPercent(resyncRequestedAt, isWaitingForResync);
  const saveRoom = useSaveOfferBuilderRoom();
  const createBatchRoomRates = useCreateBatchRoomRates();
  const createRoomTypeCapacity = useCreateRoomTypeCapacity();
  const generateBulkBeddingConfigurations = useGenerateBulkBeddingConfigurations();
  const {
    data: createPropertySchema,
    isError: hasCreatePropertySchemaError,
    isFetching: isLoadingCreatePropertySchema,
    refetch: refetchCreatePropertySchema,
  } = useGetPropertiesSchema(isPropertyStep && isCreatingProperty);
  const {
    data: ratePlanCreateSchema,
    isError: hasRatePlanCreateSchemaError,
    isFetching: isLoadingRatePlanCreateSchema,
    refetch: refetchRatePlanCreateSchema,
  } = useGetRatePlanCreateSchema(isRatePlanStep);
  const createdRatePlanIds = useMemo(
    () => createdRatePlans.map((ratePlan) => ratePlan.id).filter(Boolean),
    [createdRatePlans],
  );
  const areRatePlanDraftsComplete = ratePlanDrafts.length > 0 && !getRatePlanDraftsValidationError(ratePlanDrafts);
  const isRatePlanStepReadOnly =
    ratePlanDrafts.length > 0 && ratePlanDrafts.every((draft) => !!draft.createdRatePlanId);
  const isRoomRatesStepReadOnly = hasAppliedRoomRates;
  const appliedRatePlanNamesByRoomTypeId = useMemo(
    () =>
      appliedFlashRoomRates.reduce<Record<string, Array<string>>>((acc, roomRate) => {
        const label = roomRate.ratePlanName || roomRate.ratePlanId;
        const existingLabels = acc[roomRate.roomTypeId] ?? [];

        if (!existingLabels.includes(label)) {
          acc[roomRate.roomTypeId] = existingLabels.concat(label);
        }

        return acc;
      }, {}),
    [appliedFlashRoomRates],
  );
  const distinctAppliedRoomRates = useMemo(
    () => getDistinctAppliedRoomRates(appliedFlashRoomRates),
    [appliedFlashRoomRates],
  );
  const currentStep = STEPS[activeStep];
  const selectableProperties =
    !createdProperty || properties.some((property) => property.id === createdProperty.id)
      ? properties
      : [...properties, createdProperty];
  const activeProperties = selectableProperties.filter((property) => !property.deleted_at);
  const selectedProperty = selectableProperties.find((property) => property.id === selectedPropertyId) ?? null;
  const selectedPropertyRoomTypes = useMemo(() => getPropertyRoomTypes(selectedProperty), [selectedProperty]);
  const selectedPropertyHasRooms = selectedPropertyRoomTypes.length > 0;
  const roomDraftsToCreate = getCreatableRoomDrafts(roomDrafts);
  const appliedRoomRateRoomTypeIds = useMemo(
    () => Array.from(new Set(appliedFlashRoomRates.map((roomRate) => roomRate.roomTypeId))),
    [appliedFlashRoomRates],
  );
  const roomRateRooms = useMemo(
    () =>
      buildRoomRateRooms({
        appliedRoomTypeIds: appliedRoomRateRoomTypeIds,
        ratePlanIds: createdRatePlanIds,
        roomDrafts,
        roomTypes: selectedPropertyRoomTypes,
      }),
    [appliedRoomRateRoomTypeIds, createdRatePlanIds, roomDrafts, selectedPropertyRoomTypes],
  );
  const selectableRoomRateRoomTypeIds = useMemo(
    () => roomRateRooms.filter((room) => !room.hasRatePlan).map((room) => room.id),
    [roomRateRooms],
  );
  const selectedPropertyIdForRooms = selectedProperty?.id;
  const {
    data: roomTypesSchema,
    isError: hasRoomTypesSchemaError,
    isFetching: isLoadingRoomTypesSchema,
    refetch: refetchRoomTypesSchema,
  } = useRoomTypesSchema({
    propertyId: selectedPropertyIdForRooms,
    enabled: isRoomStep && !!selectedPropertyIdForRooms,
  });
  const { data: amenityOptionsData = [], isFetching: isLoadingAmenities } = useGetAmenities({ enabled: isRoomStep });
  const roomTypesFormSchema = useMemo<RJSFSchema | undefined>(
    () => (roomTypesSchema?.post?.body?.schema ?? roomTypesSchema?.put?.body?.schema) as RJSFSchema | undefined,
    [roomTypesSchema],
  );
  const amenityOptions = useMemo(() => {
    const fetchedAmenityOptions = (amenityOptionsData as Array<{ name?: string | null }>)
      .map((item) => item.name)
      .filter((name): name is string => typeof name === 'string' && !!name.trim());

    return Array.from(new Set<string>(fetchedAmenityOptions.concat(additionalAmenityOptions))).sort();
  }, [additionalAmenityOptions, amenityOptionsData]);
  const missingRequiredRoomFields = roomDraftsToCreate.flatMap((roomDraft, index) =>
    getMissingRequiredFieldLabels(roomTypesFormSchema, roomDraft).map(
      (fieldLabel) => `Room ${index + 1}: ${fieldLabel}`,
    ),
  );
  const hasInvalidRoomDraft = roomTypesFormSchema
    ? missingRequiredRoomFields.length > 0
    : roomDraftsToCreate.some((roomDraft) => !roomDraft.name.trim());
  const isLoadingRoomDraftForm =
    isRoomStep && roomDrafts.length > 0 && (isLoadingRoomTypesSchema || isLoadingAmenities);
  const hasRoomDraftFormError =
    isRoomStep &&
    roomDrafts.length > 0 &&
    !isLoadingRoomTypesSchema &&
    (hasRoomTypesSchemaError || !roomTypesFormSchema);
  const propertySchema = useMemo(
    () =>
      preparePropertyFormSchema(createPropertySchema, {
        type: propertyFormData.type,
        channel_manager: propertyFormData.channel_manager,
      }),
    [createPropertySchema, propertyFormData.type, propertyFormData.channel_manager],
  );
  const isLoadingPropertyForm = isCreatingProperty && isLoadingCreatePropertySchema;
  const hasPropertyFormError = isCreatingProperty && hasCreatePropertySchemaError;
  const missingRequiredPropertyFields = getMissingRequiredPropertyFields({
    isCreatePropertyStep,
    propertyFormData,
    propertySchema,
  });
  const hasMissingRequiredPropertyFields = missingRequiredPropertyFields.length > 0;
  const isPrerequisitesStepNextDisabled = isPrerequisitesStep && prerequisiteCheckStatus !== 'ready';
  const isPropertyStepNextDisabled =
    isCreatePropertyStep &&
    (isLoadingPropertyForm || hasPropertyFormError || !propertySchema || hasMissingRequiredPropertyFields);
  const isRatePlanStepNextDisabled =
    isRatePlanStep &&
    (isCreatingRatePlan || !areRatePlanDraftsComplete || isLoadingRatePlanCreateSchema || hasRatePlanCreateSchemaError);
  const isRoomStepNextDisabled =
    isRoomStep && (isCreatingRooms || isLoadingRoomDraftForm || hasRoomDraftFormError || hasInvalidRoomDraft);
  const isRoomRatesStepNextDisabled =
    isRoomRatesStep &&
    !hasAppliedRoomRates &&
    (createdRatePlans.length === 0 ||
      isCreatingRoomRates ||
      Object.values(selectedRoomRatePlanIds).every((ratePlanIds) => ratePlanIds.length === 0));
  // Resolved only by Confirm (the warning) and Retry linking (the failures) -- Save doesn't attempt either itself.
  const isPackageSetupStepNextDisabled =
    isPackageSetupStep &&
    (!hasAttemptedPackageLink ||
      isLinkingPackages ||
      packageLinkFailures.length > 0 ||
      hasUnresolvedFlexibleNightsWarning);
  const isStepBusy =
    isAdvancingStep ||
    isValidatingOpportunity ||
    isCreatingNewProperty ||
    isCreatingRooms ||
    isCreatingRatePlan ||
    isCreatingRoomRates ||
    isApplyingDefaultCapacity ||
    isSavingTaxesFees ||
    isSavingPackageSetup;
  const nextButtonLabel = getNextButtonLabel({
    isAdvancingStep,
    isCreatePropertyStep,
    isCreatingNewProperty,
    isCreatingRooms,
    isRoomStep,
    roomCreateCount: roomDraftsToCreate.length,
    isValidatingOpportunity,
  });
  const primaryButtonLabel = isRatePlanStep
    ? isCreatingRatePlan
      ? 'Creating...'
      : nextButtonLabel
    : isRoomRatesStep
      ? hasAppliedRoomRates
        ? nextButtonLabel
        : isCreatingRoomRates
          ? 'Applying...'
          : 'Apply rate plans'
      : isTaxesAndFeesStep
        ? getSaveStepButtonLabel(isSavingTaxesFees)
        : isPackageSetupStep
          ? getSaveStepButtonLabel(isSavingPackageSetup)
          : nextButtonLabel;

  useEffect(() => {
    if (
      isRoomRatesStep &&
      createdRatePlans.length > 0 &&
      roomRateRooms.length > 0 &&
      selectableRoomRateRoomTypeIds.length === 0 &&
      !hasAppliedRoomRates
    ) {
      setHasAppliedRoomRates(true);
    }
  }, [
    createdRatePlans.length,
    hasAppliedRoomRates,
    isRoomRatesStep,
    roomRateRooms.length,
    selectableRoomRateRoomTypeIds.length,
  ]);

  useEffect(() => {
    if (
      !isRoomRatesStep ||
      hasAppliedRoomRates ||
      createdRatePlans.length === 0 ||
      selectableRoomRateRoomTypeIds.length === 0
    ) {
      return;
    }

    const selectionKey = createdRatePlanIds.join(',');

    if (defaultRoomRateSelectionKey.current === selectionKey) {
      return;
    }

    defaultRoomRateSelectionKey.current = selectionKey;
    const defaultRatePlanIds = createdRatePlanIds;
    setSelectedRoomRatePlanIds(
      defaultRatePlanIds.length > 0
        ? Object.fromEntries(selectableRoomRateRoomTypeIds.map((roomTypeId) => [roomTypeId, defaultRatePlanIds]))
        : {},
    );
  }, [
    createdRatePlanIds,
    createdRatePlans.length,
    hasAppliedRoomRates,
    isRoomRatesStep,
    selectableRoomRateRoomTypeIds,
  ]);

  // Supports deep-linking into this wizard via `?setupFlashOffer=<salesforceId>`.
  useEffect(() => {
    if (urlTriggeredSalesforceOfferId && !isDialogOpen) {
      handleOpen();
      setSalesforceOfferId(urlTriggeredSalesforceOfferId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('setupFlashOffer');
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTriggeredSalesforceOfferId]);

  // Gated on resyncRequestedAt, not prerequisiteCheckStatus — this effect sets that state,
  // so depending on it cancelled the in-flight /related call before it resolved.
  useEffect(() => {
    if (!isPrerequisitesStep || !isResyncComplete || prerequisiteCheckStartedForRef.current === resyncRequestedAt) {
      return;
    }

    prerequisiteCheckStartedForRef.current = resyncRequestedAt;

    let cancelled = false;
    setPrerequisiteCheckStatus('checking');

    (async () => {
      try {
        const { data: opportunity, error } = await refetchOpportunity();

        if (cancelled) {
          return;
        }

        if (error || !opportunity?.account_18_digit_id__c) {
          setPrerequisiteCheckStatus('not-found');
          return;
        }

        if (opportunity.account_18_digit_id__c !== vendorId) {
          setPrerequisiteCheckStatus('wrong-vendor');
          return;
        }

        setValidatedOpportunity(opportunity);

        const { data: opportunityRelated } = await refetchOpportunityRelated();

        if (cancelled) {
          return;
        }

        setPreloadedOpportunityRelated(opportunityRelated);
        const hasDealOption = hasValidDealOptions(opportunityRelated?.deals);
        const hasRoomAsset = opportunityRelated?.deals?.some((deal) => !!deal.room_asset) ?? false;
        setPrerequisiteCheckStatus(hasDealOption && hasRoomAsset ? 'ready' : 'not-ready');
      } catch (error) {
        if (cancelled) {
          return;
        }

        reportError(error);
        setPrerequisiteCheckStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isPrerequisitesStep,
    isResyncComplete,
    resyncRequestedAt,
    refetchOpportunity,
    refetchOpportunityRelated,
    vendorId,
  ]);

  async function handleLinkPackages() {
    setHasAttemptedPackageLink(true);
    setIsLinkingPackages(true);
    setErrorMessage(null);

    const { failures, matchedPackages: matched } = await assignRoomRatesToPackages();

    setIsLinkingPackages(false);
    setMatchedPackages(matched);

    return failures;
  }

  // Same readiness check as assignRoomRatesToPackages, so it can't fire early and get stuck
  // permanently no-op'd (hasAttemptedPackageLink locked true with no retry). Includes
  // salesforceRooms since buildFlashPackageRoomRateAssignments needs it for name-matching.
  useEffect(() => {
    if (
      !isPackageSetupStep ||
      hasAttemptedPackageLink ||
      isLinkingPackages ||
      !selectedProperty?.id ||
      appliedFlashRoomRates.length === 0 ||
      salesforceRooms.length === 0
    ) {
      return;
    }

    void handleLinkPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPackageSetupStep,
    hasAttemptedPackageLink,
    isLinkingPackages,
    selectedProperty?.id,
    appliedFlashRoomRates.length,
    salesforceRooms.length,
  ]);

  const prerequisiteItems: Array<PrerequisiteItem> = useMemo(() => {
    const hasPrerequisiteCheckFailed =
      hasResyncFailed ||
      prerequisiteCheckStatus === 'not-found' ||
      prerequisiteCheckStatus === 'wrong-vendor' ||
      prerequisiteCheckStatus === 'error';
    const hasPrerequisiteCheckFinished = prerequisiteCheckStatus === 'ready' || prerequisiteCheckStatus === 'not-ready';

    const statusFor = (isFactTrue: boolean): PrerequisiteStatus => {
      if (hasPrerequisiteCheckFailed) {
        return 'unmet';
      }

      if (hasPrerequisiteCheckFinished) {
        return isFactTrue ? 'met' : 'unmet';
      }

      return 'pending';
    };

    const opportunityStatus = statusFor(true);
    const opportunityDetail =
      prerequisiteCheckStatus === 'not-found'
        ? "This Salesforce offer ID couldn't be found. Double-check the ID and try again."
        : prerequisiteCheckStatus === 'wrong-vendor'
          ? 'This Salesforce offer does not belong to this vendor.'
          : undefined;

    const deals = preloadedOpportunityRelated?.deals ?? [];
    const hasDealOption = hasValidDealOptions(deals);
    const hasRoomAsset = deals.some((deal) => !!deal.room_asset);

    const dealOptionDetail =
      'No Deal Option is linked to this Opportunity in Salesforce yet. Add at least one Deal Option.';
    const roomAssetDetail =
      "None of this Opportunity's Deal Options are linked to a Room Asset in Salesforce. Set Room_Asset__c on at least one Deal Option.";

    return [
      { label: 'A valid Opportunity in Salesforce', status: opportunityStatus, detail: opportunityDetail },
      {
        label: 'Linked to at least 1 valid Deal Option',
        status: statusFor(hasDealOption),
        detail: dealOptionDetail,
      },
      {
        label: 'Linked to at least 1 valid Room Asset',
        status: statusFor(hasRoomAsset),
        detail: roomAssetDetail,
      },
    ];
  }, [hasResyncFailed, preloadedOpportunityRelated, prerequisiteCheckStatus]);

  useEffect(() => {
    if (isDialogOpen && isContentStep && contentPageUrl && isResyncComplete) {
      navigate(contentPageUrl);
    }
  }, [contentPageUrl, isContentStep, isDialogOpen, isResyncComplete, navigate]);

  function goToContentPage() {
    if (contentPageUrl && isResyncComplete) {
      navigate(contentPageUrl);
    }
  }

  async function retryResync() {
    if (!trimmedSalesforceOfferId) {
      return;
    }

    try {
      const resyncResponse = await crmResyncMutation.mutateAsync(trimmedSalesforceOfferId);
      setResyncRequestedAt(resyncResponse.requestedAt);
      setPrerequisiteCheckStatus('idle');
      setErrorMessage(null);
    } catch (resyncError) {
      setErrorMessage(`Unable to start a Salesforce resync. ${getErrorMessage(resyncError)}`);
    }
  }

  function handleOpen() {
    setIsDialogOpen(true);
    setIsExitConfirmationOpen(false);
    setIsCreatingNewProperty(false);
    setIsCreatingRatePlan(false);
    setIsCreatingRooms(false);
    setIsCreatingRoomRates(false);
    setIsSavingTaxesFees(false);
    setHasAppliedRoomRates(false);
    setIsValidatingOpportunity(false);
    setResyncRequestedAt(undefined);
    setPrerequisiteCheckStatus('idle');
    setValidatedOpportunity(undefined);
    setPreloadedOpportunityRelated(undefined);
    setIsAdvancingStep(false);
    validationRunId.current += 1;
    prerequisiteCheckStartedForRef.current = undefined;
    manualRoomDraftCounter.current = 0;
    ratePlanDraftCounter.current = 0;
    defaultRoomRateSelectionKey.current = null;
    setSalesforceOfferId('');
    setErrorMessage(null);
    setPropertyWarningMessage(null);
    setActiveStep(0);
    setSelectedPropertyId(defaultPropertyId);
    setCreatedProperty(null);
    setCreatedRatePlans([]);
    initializeRatePlanDrafts();
    setAdditionalAmenityOptions([]);
    setSalesforceRooms([]);
    setPropertyFormData({});
    setRoomDrafts([]);
    setAppliedFlashRoomRates([]);
    setSelectedRoomRatePlanIds({});
  }

  function handleClose() {
    if (isStepBusy) {
      return;
    }

    if (activeStep === 0 && !trimmedSalesforceOfferId) {
      handleConfirmClose();
      return;
    }

    setIsExitConfirmationOpen(true);
  }

  function handleCancelClose() {
    setIsExitConfirmationOpen(false);
  }

  function handleConfirmClose() {
    validationRunId.current += 1;
    setIsCreatingNewProperty(false);
    setIsCreatingRatePlan(false);
    setIsCreatingRooms(false);
    setIsCreatingRoomRates(false);
    setIsSavingTaxesFees(false);
    setHasAppliedRoomRates(false);
    setIsValidatingOpportunity(false);
    setResyncRequestedAt(undefined);
    setPrerequisiteCheckStatus('idle');
    setIsAdvancingStep(false);
    setIsExitConfirmationOpen(false);
    setIsDialogOpen(false);
  }

  function handlePrevious() {
    setErrorMessage(null);
    setActiveStep((prev) => (prev === 4 && selectedPropertyHasRooms ? 2 : Math.max(prev - 1, 0)));
  }

  function handlePropertyCreateStart() {
    setErrorMessage(null);
    setIsCreatingNewProperty(true);
  }

  function handlePropertyCreateError() {
    setIsCreatingNewProperty(false);
    setErrorMessage('Unable to create property.');
  }

  async function getRoomsWithBeddingConfigurations(rooms: Array<SalesforceRoom>) {
    const bedGroupsByDealId = new Map<string, SalesforceRoom['bed_groups']>();
    const dealIdByRoomAssetId = new Map<string, string>();
    let hasBeddingConfigurationError = false;

    rooms.forEach((room) => {
      const roomAssetId = room.room_asset?.sfid || room.room_asset?.id;
      const roomAssetKey = roomAssetId ? String(roomAssetId) : '';
      const dealId = room.id ? String(room.id) : '';

      if (roomAssetKey && dealId && !dealIdByRoomAssetId.has(roomAssetKey)) {
        dealIdByRoomAssetId.set(roomAssetKey, dealId);
      }
    });

    const dealIds = Array.from(dealIdByRoomAssetId.values());

    for (let index = 0; index < dealIds.length; index += 10) {
      let response;

      try {
        response = await generateBulkBeddingConfigurations.mutateAsync(dealIds.slice(index, index + 10));
      } catch (error) {
        reportError(error);
        hasBeddingConfigurationError = true;
        continue;
      }

      response.result?.forEach((result) => {
        if (result.status !== 200) {
          hasBeddingConfigurationError = true;
          return;
        }

        const bedGroups = mapGeneratedBeddingConfigurationToBedGroups({ result: result.result });

        if (bedGroups.length > 0) {
          bedGroupsByDealId.set(result.deal_offer_id, bedGroups);
        }
      });
    }

    if (hasBeddingConfigurationError) {
      setErrorMessage('Some bedding configurations could not be prefilled. Review room bedding before continuing.');
    }

    return rooms.map((room) => {
      const bedGroups = bedGroupsByDealId.get(String(room.id));
      return bedGroups ? { ...room, bed_groups: bedGroups } : room;
    });
  }

  async function prepareRoomDrafts(property: FlashOfferProperty | null, existingRoomTypes: Array<App.RoomType> = []) {
    if (!property?.id) {
      setRoomDrafts([]);
      setUnmatchedSalesforceRoomNames([]);
      return [];
    }

    const substringMissingRooms = getSalesforceRoomsMissingExistingRoomTypes(salesforceRooms, existingRoomTypes);
    let nameOverrides = aiRoomNameOverrides;

    if (substringMissingRooms.length > 0 && existingRoomTypes.length > 0) {
      const resolvedOverrides = await resolveRoomNameMatchesWithAi(
        substringMissingRooms.map((room) => room.room_asset?.name),
        existingRoomTypes.map((roomType) => roomType.name),
      );

      if (Object.keys(resolvedOverrides).length > 0) {
        nameOverrides = { ...aiRoomNameOverrides, ...resolvedOverrides };
        setAiRoomNameOverrides(nameOverrides);
      }
    }

    const missingSalesforceRooms = getSalesforceRoomsMissingExistingRoomTypes(
      salesforceRooms,
      existingRoomTypes,
      nameOverrides,
    );

    if (missingSalesforceRooms.length === 0) {
      setRoomDrafts([]);
      setUnmatchedSalesforceRoomNames([]);
      return [];
    }

    const rooms = await getRoomsWithBeddingConfigurations(missingSalesforceRooms);
    const roomDrafts = mapSalesforceRoomsToRoomDrafts({
      propertyCategory: property.category,
      propertyId: property.id,
      rooms,
    });

    setRoomDrafts(roomDrafts);
    // Tracked separately so these names stay visible even after the CM deletes every draft.
    setUnmatchedSalesforceRoomNames(roomDrafts.map((draft) => draft.name).filter(Boolean));
    return roomDrafts;
  }

  async function handlePropertyCreateSuccess(property: FlashOfferProperty) {
    if (!property?.id) {
      handlePropertyCreateError();
      return;
    }

    setCreatedProperty(property);
    setSelectedPropertyId(property.id);
    setErrorMessage(null);
    setIsAdvancingStep(true);

    try {
      await prepareRoomDrafts(property);
      await refreshProperties(property);
      setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    } finally {
      setIsAdvancingStep(false);
      setIsCreatingNewProperty(false);
    }
  }

  function updateRoomDraft(draftId: string, updates: Partial<FlashOfferRoomDraft>) {
    setRoomDrafts((prev) =>
      prev.map((roomDraft) => (roomDraft.draftId === draftId ? { ...roomDraft, ...updates } : roomDraft)),
    );
  }

  function handleAddAmenityOption(amenityName: string) {
    const trimmedAmenityName = amenityName.trim();

    if (!trimmedAmenityName) {
      return;
    }

    setAdditionalAmenityOptions((prev) => (prev.includes(trimmedAmenityName) ? prev : prev.concat(trimmedAmenityName)));
  }

  function handleAddRoomDraft() {
    const propertyId = selectedProperty?.id;

    if (!propertyId) {
      setErrorMessage('Select or create a property before adding rooms.');
      return;
    }

    manualRoomDraftCounter.current += 1;
    setRoomDrafts((prev) => [
      ...prev,
      buildBlankRoomDraft({
        draftId: `manual-room-${manualRoomDraftCounter.current}`,
        propertyCategory: selectedProperty.category,
        propertyId,
      }),
    ]);
  }

  function handleRemoveRoomDraft(draftId: string) {
    setRoomDrafts((prev) => prev.filter((roomDraft) => roomDraft.draftId !== draftId));
  }

  function markRoomDraftsCreated(savedRoomsByDraftId: Map<string, App.RoomType>) {
    setRoomDrafts((prev) =>
      prev.map((roomDraft) => {
        const savedRoom = savedRoomsByDraftId.get(roomDraft.draftId);

        if (!savedRoom) {
          return roomDraft;
        }

        return {
          ...roomDraft,
          id: savedRoom.id,
          existingRoomName: savedRoom.name || roomDraft.name,
          shouldCreate: false,
        };
      }),
    );
  }

  async function createSelectedRooms() {
    const propertyId = selectedProperty?.id;

    if (!propertyId) {
      setErrorMessage('Select or create a property before creating rooms.');
      return false;
    }

    if (hasRoomDraftFormError) {
      setErrorMessage('Unable to load room fields.');
      return false;
    }

    if (hasInvalidRoomDraft) {
      setErrorMessage(`Complete mandatory room fields: ${missingRequiredRoomFields.join(', ') || 'Name'}.`);
      return false;
    }

    if (roomDraftsToCreate.length === 0) {
      return true;
    }

    const savedRoomsByDraftId = new Map<string, App.RoomType>();

    setIsCreatingRooms(true);
    setErrorMessage(null);

    try {
      for (const roomDraft of roomDraftsToCreate) {
        const savedRoom = await saveRoom.mutateAsync({
          offerId: trimmedSalesforceOfferId,
          propertyId,
          room: buildRoomCreatePayload(roomDraft, propertyId),
          suppressNotifications: true,
        });
        savedRoomsByDraftId.set(roomDraft.draftId, savedRoom);
      }

      markRoomDraftsCreated(savedRoomsByDraftId);
      await refreshProperties(selectedProperty ?? undefined, Array.from(savedRoomsByDraftId.values()));
      return true;
    } catch (error) {
      markRoomDraftsCreated(savedRoomsByDraftId);
      setErrorMessage(`Unable to create rooms. ${getErrorMessage(error)}`);
      return false;
    } finally {
      setIsCreatingRooms(false);
    }
  }

  async function refreshProperties(property?: FlashOfferProperty, roomTypes?: Array<App.RoomType>) {
    try {
      await onPropertiesRefresh?.(property, roomTypes);
    } catch (error) {
      reportError(error);
    }
  }

  function generateRatePlanDraftId() {
    ratePlanDraftCounter.current += 1;
    return `rate-plan-draft-${ratePlanDraftCounter.current}`;
  }

  const initializeRatePlanDrafts = useCallback(
    ({
      ratePlanCancellationPolicy = '',
      ratePlanName = getDefaultFlashRatePlanName(),
    }: {
      ratePlanCancellationPolicy?: string;
      ratePlanName?: string;
    } = {}) => {
      defaultRatePlanDraftValues.current = {
        ratePlanCancellationPolicy,
        ratePlanName,
      };

      const draft = buildDefaultRatePlanDraft({
        draftId: generateRatePlanDraftId(),
        ratePlanCancellationPolicy,
        ratePlanName,
      });

      setRatePlanDrafts([draft]);
      setExpandedRatePlanDraftIds([draft.draftId]);
      setCreatedRatePlans([]);
    },
    [],
  );

  useEffect(() => {
    if (isRatePlanStep && ratePlanDrafts.length === 0) {
      initializeRatePlanDrafts();
    }
  }, [initializeRatePlanDrafts, isRatePlanStep, ratePlanDrafts.length]);

  function updateRatePlanDraft(draftId: string, updates: Partial<FlashRatePlanDraft>) {
    if (isRatePlanStepReadOnly) {
      return;
    }

    setRatePlanDrafts((prev) =>
      prev.map((draft) => {
        if (draft.draftId !== draftId || isCreatedRatePlanDraft(draft)) {
          return draft;
        }

        return { ...draft, ...updates };
      }),
    );
  }

  function addRatePlanDraft() {
    if (isRatePlanStepReadOnly) {
      return;
    }

    const { ratePlanCancellationPolicy, ratePlanName } = defaultRatePlanDraftValues.current;
    const draftId = generateRatePlanDraftId();

    setRatePlanDrafts((prev) => {
      const draftNumber = prev.length + 1;
      const uniqueRatePlanName = draftNumber === 1 ? ratePlanName : `${ratePlanName} ${draftNumber}`;

      return prev.concat(
        buildDefaultRatePlanDraft({
          draftId,
          ratePlanCancellationPolicy,
          ratePlanName: uniqueRatePlanName,
        }),
      );
    });
    setExpandedRatePlanDraftIds((prev) => Array.from(new Set(prev.concat(draftId))));
    setErrorMessage(null);
  }

  function removeRatePlanDraft(draftId: string) {
    if (isRatePlanStepReadOnly || ratePlanDrafts.length <= 1) {
      return;
    }

    const removedDraft = ratePlanDrafts.find((draft) => draft.draftId === draftId);

    if (!removedDraft || isCreatedRatePlanDraft(removedDraft)) {
      return;
    }

    setRatePlanDrafts((prev) => prev.filter((draft) => draft.draftId !== draftId));
    setExpandedRatePlanDraftIds((prev) => prev.filter((id) => id !== draftId));
    setErrorMessage(null);
  }

  function toggleRatePlanDraftExpanded(draftId: string, expanded: boolean) {
    setExpandedRatePlanDraftIds((prev) =>
      expanded ? Array.from(new Set(prev.concat(draftId))) : prev.filter((id) => id !== draftId),
    );
  }

  async function createRatePlansFromDrafts() {
    const validationError = getRatePlanDraftsValidationError(ratePlanDrafts);

    if (validationError) {
      setErrorMessage(validationError);
      return false;
    }

    const pendingDrafts = ratePlanDrafts.filter((draft) => !draft.createdRatePlanId);

    if (pendingDrafts.length === 0) {
      return true;
    }

    setIsCreatingRatePlan(true);
    setErrorMessage(null);

    const createdPlans: Array<App.RatePlan> = [];
    const nextDrafts = [...ratePlanDrafts];

    try {
      for (let index = 0; index < nextDrafts.length; index += 1) {
        const draft = nextDrafts[index];

        if (draft.createdRatePlanId) {
          continue;
        }

        const response = await ReservationService.createRatePlan({
          id_salesforce_external: vendorId,
          name: draft.ratePlanName.trim(),
          cancellation_policy: draft.ratePlanCancellationPolicy.trim(),
          status: draft.ratePlanStatus.trim(),
          product_type: FLASH_RATE_PLAN_PRODUCT_TYPE,
          rate_type: draft.ratePlanRateType.trim(),
        });

        if (!response?.result?.id) {
          throw new Error('Unexpected response from createRatePlan');
        }

        nextDrafts[index] = {
          ...draft,
          createdRatePlanId: response.result.id,
        };
        createdPlans.push(response.result);
      }

      setRatePlanDrafts(nextDrafts);
      setCreatedRatePlans((prev) => buildCreatedRatePlansFromDrafts(prev, createdPlans, nextDrafts));
      return true;
    } catch (error) {
      if (createdPlans.length > 0) {
        setRatePlanDrafts(nextDrafts);
        setCreatedRatePlans((prev) => buildCreatedRatePlansFromDrafts(prev, createdPlans, nextDrafts));
      }

      reportError(error);
      const createdCount = createdPlans.length;

      setErrorMessage(
        createdCount > 0
          ? `Created ${createdCount} of ${pendingDrafts.length} rate plans. Created plans are locked — fix the remaining plan(s) and click Next to retry. ${getErrorMessage(error)}`
          : `Unable to create rate plans. ${getErrorMessage(error)}`,
      );
      return false;
    } finally {
      setIsCreatingRatePlan(false);
    }
  }

  function handleSelectRoomRatePlans(roomTypeId: string, ratePlanIds: Array<string>) {
    if (isRoomRatesStepReadOnly) {
      return;
    }

    setErrorMessage(null);
    setSelectedRoomRatePlanIds((prev) => {
      if (ratePlanIds.length === 0) {
        const next = { ...prev };
        delete next[roomTypeId];
        return next;
      }

      return { ...prev, [roomTypeId]: ratePlanIds };
    });
  }

  async function applyRoomRates() {
    if (createdRatePlans.length === 0) {
      setErrorMessage('Create at least one Flash rate plan before applying room rates.');
      return false;
    }

    const selectedRoomRateAssignments = Object.entries(selectedRoomRatePlanIds).filter(
      ([, ratePlanIds]) => ratePlanIds.length > 0,
    );

    if (selectedRoomRateAssignments.length === 0) {
      setErrorMessage('Select at least one room.');
      return false;
    }

    const propertyId = selectedProperty?.id;

    if (!propertyId) {
      setErrorMessage('Select or create a property before applying room rates.');
      return false;
    }

    setIsCreatingRoomRates(true);
    setErrorMessage(null);

    const createdRoomRates: Array<AppliedFlashRoomRate> = [];
    const appliedRoomRateIds = new Set(
      appliedFlashRoomRates.map((roomRate) => `${roomRate.roomTypeId}:${roomRate.ratePlanId}`),
    );
    const pendingRoomRateAssignments = selectedRoomRateAssignments.flatMap(([roomTypeId, ratePlanIds]) =>
      ratePlanIds
        .map((ratePlanId) => {
          const ratePlan = createdRatePlans.find((plan) => plan.id === ratePlanId);

          if (!ratePlan?.id || appliedRoomRateIds.has(`${roomTypeId}:${ratePlan.id}`)) {
            return null;
          }

          return { roomTypeId, ratePlan };
        })
        .filter((assignment): assignment is { roomTypeId: string; ratePlan: App.RatePlan } => !!assignment),
    );

    const pendingAssignmentsByRoomTypeId = new Map<string, Array<App.RatePlan>>();

    pendingRoomRateAssignments.forEach(({ roomTypeId, ratePlan }) => {
      const ratePlans = pendingAssignmentsByRoomTypeId.get(roomTypeId) ?? [];
      pendingAssignmentsByRoomTypeId.set(roomTypeId, ratePlans.concat(ratePlan));
    });

    try {
      for (const [roomTypeId, ratePlans] of pendingAssignmentsByRoomTypeId) {
        const ratePlanIds = ratePlans.map((ratePlan) => ratePlan.id);
        const response = await createBatchRoomRates.mutateAsync({
          propertyId,
          ratePlanIds,
          roomTypeId,
        });
        const allResults = response?.result;
        const ratePlanIdSet = new Set(ratePlanIds);
        const roomRateByRatePlanId = new Map<string, App.RoomRate>();

        if (Array.isArray(allResults)) {
          allResults.forEach((roomRate) => {
            const ratePlanId = roomRate?.rate_plan?.id;

            if (roomRate?.id && ratePlanId && ratePlanIdSet.has(ratePlanId) && !roomRateByRatePlanId.has(ratePlanId)) {
              roomRateByRatePlanId.set(ratePlanId, roomRate);
            }
          });
        }

        const results = ratePlanIds
          .map((ratePlanId) => roomRateByRatePlanId.get(ratePlanId))
          .filter((roomRate): roomRate is App.RoomRate => !!roomRate);

        if (!Array.isArray(allResults) || results.length !== ratePlanIds.length) {
          throw new Error('Unexpected response from createBatchRoomRates');
        }

        results.forEach((roomRate) => {
          if (!roomRate?.id) {
            throw new Error('Unexpected response from createBatchRoomRates');
          }

          const ratePlanId = roomRate.rate_plan?.id;
          const ratePlan = ratePlans.find((plan) => plan.id === ratePlanId);

          if (!ratePlanId || !ratePlan) {
            throw new Error('Unexpected response from createBatchRoomRates');
          }

          appliedRoomRateIds.add(`${roomTypeId}:${ratePlanId}`);
          createdRoomRates.push({
            ratePlanId,
            ratePlanName: ratePlan.name,
            roomName: roomRateRooms.find((room) => room.id === roomTypeId)?.name || roomTypeId,
            roomRateId: roomRate.id,
            roomTypeId,
          });
        });
      }

      setAppliedFlashRoomRates((prev) => prev.concat(createdRoomRates));
      setHasAppliedRoomRates(true);
      return true;
    } catch (error) {
      setAppliedFlashRoomRates((prev) => prev.concat(createdRoomRates));
      setSelectedRoomRatePlanIds((prev) => {
        const next = { ...prev };

        createdRoomRates.forEach(({ roomTypeId, ratePlanId }) => {
          const remainingRatePlanIds = (next[roomTypeId] ?? []).filter((id) => id !== ratePlanId);

          if (remainingRatePlanIds.length === 0) {
            delete next[roomTypeId];
          } else {
            next[roomTypeId] = remainingRatePlanIds;
          }
        });

        return next;
      });
      reportError(error);
      const createdCount = createdRoomRates.length;

      setErrorMessage(
        createdCount > 0
          ? `Applied ${createdCount} of ${pendingRoomRateAssignments.length} room rate assignments. Applied assignments are saved — fix the remaining selection and click Next to retry. ${getErrorMessage(error)}`
          : `Unable to apply rate plans. ${getErrorMessage(error)}`,
      );
      return false;
    } finally {
      setIsCreatingRoomRates(false);
    }
  }

  async function applyDefaultCapacities(targetRoomRates: Array<CapacityRoomRate>) {
    const propertyId = selectedProperty?.id;

    if (!propertyId || targetRoomRates.length === 0) {
      return;
    }

    setIsApplyingDefaultCapacity(true);
    const targetRoomRateIds = new Set(targetRoomRates.map((roomRate) => roomRate.roomRateId));
    setCapacityFailedRoomRateIds((prev) => prev.filter((id) => !targetRoomRateIds.has(id)));

    const failedRoomRateIds: Array<string> = [];

    await runInChunks(
      targetRoomRates,
      async (roomRate) => {
        // Each combination gets its own try/catch so a retry that re-hits an already-created
        // combination (e.g. a duplicate-key rejection) doesn't stop the still-missing one from
        // being attempted -- an enclosing try around the whole loop would abort on the first error.
        let hasFailedCombination = false;

        for (const capacity of DEFAULT_CAPACITY_COMBINATIONS) {
          try {
            await createRoomTypeCapacity.mutateAsync({
              capacity,
              propertyId,
              roomRateId: roomRate.roomRateId,
              roomTypeId: roomRate.roomTypeId,
            });
          } catch (error) {
            reportError(error);
            hasFailedCombination = true;
          }
        }

        if (hasFailedCombination) {
          throw new Error(`Failed to apply default capacity to room rate ${roomRate.roomRateId}`);
        }
      },
      {
        chunkSize: 5,
        failureMode: 'isolate',
        onError: (_error, roomRate) => {
          if (roomRate) {
            failedRoomRateIds.push(roomRate.roomRateId);
          }
        },
      },
    );

    setIsApplyingDefaultCapacity(false);
    // A failed retry must never erase a prior real success -- only add newly-succeeded ids, never remove.
    const failedRoomRateIdSet = new Set(failedRoomRateIds);
    const succeededRoomRateIds = targetRoomRates
      .map((roomRate) => roomRate.roomRateId)
      .filter((id) => !failedRoomRateIdSet.has(id));
    setCapacityAppliedRoomRateIds((prev) => Array.from(new Set(prev.concat(succeededRoomRateIds))));
    setCapacityFailedRoomRateIds((prev) => prev.concat(failedRoomRateIds));

    if (failedRoomRateIds.length === 0) {
      enqueueSnackbar(
        `Default capacity (1A / 2A) applied to ${targetRoomRates.length} room rate${targetRoomRates.length === 1 ? '' : 's'}.`,
        { variant: 'success' },
      );
    } else {
      enqueueSnackbar(
        `Failed to apply default capacity to ${failedRoomRateIds.length} room rate${failedRoomRateIds.length === 1 ? '' : 's'}.`,
        { variant: 'error' },
      );
    }
  }

  async function assignRoomRatesToPackages(): Promise<{
    failures: Array<string>;
    matchedPackages: Array<App.Package>;
  }> {
    if (!selectedProperty?.id || appliedFlashRoomRates.length === 0) {
      return { failures: [], matchedPackages: [] };
    }

    setIsAdvancingStep(true);
    setErrorMessage(null);

    try {
      const response = await OffersService.getOffer(trimmedSalesforceOfferId);
      const offerPackages = response?.result?.packages ?? [];
      const {
        assignableRoomTypes: linkedAssignableRoomTypes,
        assignments,
        unmatchedPackageNames,
        unmatchedPackages: linkedUnmatchedPackages,
      } = buildFlashPackageRoomRateAssignments({
        aiRoomNameOverrides,
        manualRoomTypeIdByPackageId: manualPackageRoomTypeIds,
        offerPackages,
        propertyId: selectedProperty.id,
        relatedPackageRooms: salesforceRooms,
        roomRates: appliedFlashRoomRates,
        roomTypes: selectedPropertyRoomTypes,
      });

      setAssignableRoomTypes(linkedAssignableRoomTypes);
      setUnmatchedPackages(linkedUnmatchedPackages);

      const results = await Promise.allSettled(
        assignments.map((assignment) =>
          PackagesService.updatePackage(assignment.packageData, trimmedSalesforceOfferId, assignment.packageId),
        ),
      );

      const updateFailureMessages: Array<string> = [];

      results.forEach((result, index) => {
        if (result.status !== 'rejected') {
          return;
        }

        reportError(result.reason);
        const matchedPackage = offerPackages.find((pkg) => getPackagePersistId(pkg) === assignments[index].packageId);
        const packageLabel = matchedPackage ? getOfferPackageName(matchedPackage) : assignments[index].packageId;
        const detail = getPackageUpdateErrorDetail(result.reason, getErrorMessage(result.reason));
        updateFailureMessages.push(`${packageLabel}: ${detail}`);
      });

      const failedPackageCount = unmatchedPackageNames.length + updateFailureMessages.length;

      if (failedPackageCount > 0) {
        enqueueSnackbar(`Unable to link ${failedPackageCount} package${failedPackageCount === 1 ? '' : 's'}`, {
          variant: 'warning',
        });
      }

      // A matched pair can still fail on the update itself, independent of room-name matching.
      const failures = unmatchedPackageNames.concat(updateFailureMessages);

      setMatchedRoomRateCount(assignments.length);
      setPackageLinkFailures(failures);

      // Include packages that failed to save too, so their settings stay editable below.
      const matchedPackageIds = new Set(assignments.map((assignment) => assignment.packageId));

      return {
        failures,
        matchedPackages: offerPackages.filter((pkg) => {
          const id = getPackagePersistId(pkg);
          return !!id && matchedPackageIds.has(id);
        }),
      };
    } catch (error) {
      reportError(error);
      const failures = ['Unable to link packages to rooms.'];
      setMatchedRoomRateCount(0);
      setPackageLinkFailures(failures);
      setAssignableRoomTypes([]);
      setUnmatchedPackages([]);
      return { failures, matchedPackages: [] };
    } finally {
      setIsAdvancingStep(false);
    }
  }

  async function handleNext() {
    if (isStepBusy) {
      return;
    }

    if (activeStep === 0 && !isSalesforceOpportunityId(trimmedSalesforceOfferId)) {
      setErrorMessage('Enter a valid Salesforce offer ID.');
      return;
    }

    if (activeStep === 0) {
      const runId = validationRunId.current + 1;
      validationRunId.current = runId;
      setIsValidatingOpportunity(true);

      // existence/vendor-ownership validation deferred to Prerequisites/valid-opportunity rule
      try {
        const resyncResponse = await crmResyncMutation.mutateAsync(trimmedSalesforceOfferId);
        setResyncRequestedAt(resyncResponse.requestedAt);
      } catch (resyncError) {
        setIsValidatingOpportunity(false);
        setErrorMessage(`Unable to start a Salesforce resync. ${getErrorMessage(resyncError)}`);
        return;
      }

      if (runId !== validationRunId.current) {
        return;
      }

      setPrerequisiteCheckStatus('idle');
      setValidatedOpportunity(undefined);
      setPreloadedOpportunityRelated(undefined);
      setIsValidatingOpportunity(false);
    }

    if (isPrerequisitesStep) {
      if (prerequisiteCheckStatus !== 'ready' || !validatedOpportunity) {
        return;
      }

      const { result: existingOffer } = await OffersService.getOffer(trimmedSalesforceOfferId).catch(() => ({
        result: undefined,
      }));

      setPropertyWarningMessage(
        existingOffer?.packages?.some((pkg: App.BasePackage) => pkg?.fk_property_id)
          ? 'This offer is already associated with a property. Continuing may relink or overwrite its existing package/room/rate assignments.'
          : null,
      );

      const cancellationPolicy =
        mapSFCancellationPolicyToAdmin(validatedOpportunity.functional_details?.cancellation_policy__c ?? undefined) ??
        '';
      const opportunityRelated = preloadedOpportunityRelated;
      const travelStartRatePlanName = getFlashRatePlanNameFromTravelStart(opportunityRelated?.deals);

      initializeRatePlanDrafts({
        ratePlanCancellationPolicy: cancellationPolicy,
        ratePlanName: travelStartRatePlanName || getDefaultFlashRatePlanName(),
      });

      setPropertyFormData({
        id_salesforce_external: vendorId,
        ...mapVendorBillingToPropertyFormData(vendor),
        logo_id_cloudinary_external: DEFAULT_NEW_PROPERTY_LOGO_ID,
        ...mapRedemptionOptionsToPropertyFormData(validatedOpportunity.key_commercial?.redemption_options__c),
        ...mapExtraGuestSurchargeAgeRangesToPropertyFormData(opportunityRelated?.extra_guest_surcharges),
      });
      setSalesforceRooms(opportunityRelated?.deals ?? opportunityRelated?.rooms ?? []);
      setRoomDrafts([]);
    }

    if (isPropertyStep && isCreatingNewProperty) {
      return;
    }

    if (isPropertyStep && isLoadingPropertyForm) {
      setErrorMessage('Wait for property details to finish loading.');
      return;
    }

    if (isPropertyStep && missingRequiredPropertyFields.length > 0) {
      setErrorMessage(`Complete mandatory property fields: ${missingRequiredPropertyFields.join(', ')}.`);
      return;
    }

    if (isPropertyStep && isCreatingProperty && !propertySchema) {
      setErrorMessage('Unable to validate property details.');
      return;
    }

    if (isPropertyStep && !isCreatingProperty) {
      setIsAdvancingStep(true);

      try {
        const preparedRoomDrafts = await prepareRoomDrafts(selectedProperty, selectedPropertyRoomTypes);

        if (selectedPropertyHasRooms && preparedRoomDrafts.length === 0) {
          // No rooms to create means createSelectedRooms' refresh never runs — without this,
          // stale room-type data breaks name-matching in assignRoomRatesToPackages.
          await refreshProperties(selectedProperty ?? undefined);
          setErrorMessage(null);
          setActiveStep(4);
          return;
        }
      } finally {
        setIsAdvancingStep(false);
      }
    }

    if (isRoomStep) {
      const roomsCreated = await createSelectedRooms();

      if (!roomsCreated) {
        return;
      }
    }

    if (isRatePlanStep) {
      const ratePlansCreated = await createRatePlansFromDrafts();

      if (!ratePlansCreated) {
        return;
      }
    }

    if (isRoomRatesStep && !hasAppliedRoomRates) {
      const roomRatesCreated = await applyRoomRates();

      if (!roomRatesCreated) {
        return;
      }
    }

    const shouldPreserveRoomDraftWarning = isPropertyStep && !isCreatingProperty && !selectedPropertyHasRooms;
    if (!shouldPreserveRoomDraftWarning) {
      setErrorMessage(null);
    }
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }

  return (
    <>
      <Button variant="contained" onClick={handleOpen} disabled={disabled}>
        Set up Flash offer
      </Button>

      <Dialog open={isDialogOpen} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center' }}>
          Set up Flash offer
          <IconButton
            aria-label="Close"
            disabled={isStepBusy}
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {propertyWarningMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {propertyWarningMessage}
            </Alert>
          )}

          <Stack spacing={2}>
            <Typography variant="h6">{currentStep.title}</Typography>
            {!isPackageSetupStep && (
              <Typography variant="body2" color="text.secondary">
                {currentStep.helperText}
              </Typography>
            )}

            {activeStep === 0 && (
              <TextField
                label="Salesforce offer ID"
                value={salesforceOfferId}
                onChange={(event) => {
                  setSalesforceOfferId(event.target.value);
                  initializeRatePlanDrafts();
                  setErrorMessage(null);
                }}
                required
                fullWidth
                autoFocus
                disabled={isStepBusy}
              />
            )}

            {isPrerequisitesStep && (
              <PrerequisitesStep
                hasSyncFailed={hasResyncFailed}
                isSyncing={isWaitingForResync || prerequisiteCheckStatus === 'checking'}
                items={prerequisiteItems}
                onRetrySync={retryResync}
                syncFailureMessage={resyncStatus?.message ?? undefined}
                syncProgressPercent={resyncProgressPercent}
              />
            )}

            {isPropertyStep && (
              <PropertyStep
                activeProperties={activeProperties}
                hasPropertyFormError={hasPropertyFormError}
                isBusy={isStepBusy}
                isCreatingProperty={isCreatingProperty}
                isLoadingPropertyForm={isLoadingPropertyForm}
                onDraftChange={setPropertyFormData}
                onPropertyCreateError={handlePropertyCreateError}
                onPropertyCreateStart={handlePropertyCreateStart}
                onPropertyCreateSuccess={handlePropertyCreateSuccess}
                onPropertySelect={(propertyId) => {
                  setErrorMessage(null);
                  setSelectedPropertyId(propertyId);
                  setRoomDrafts([]);
                }}
                onRetryPropertySchema={() => {
                  setErrorMessage(null);
                  refetchCreatePropertySchema();
                }}
                propertyFormData={propertyFormData}
                propertySchema={propertySchema}
                selectedPropertyId={selectedPropertyId}
                vendorId={vendorId}
              />
            )}

            {isRatePlanStep && (
              <RatePlanStep
                disabled={isStepBusy}
                expandedRatePlanDraftIds={expandedRatePlanDraftIds}
                hasSchemaError={hasRatePlanCreateSchemaError}
                isLoadingSchema={isLoadingRatePlanCreateSchema}
                onAddDraft={addRatePlanDraft}
                onClearError={() => setErrorMessage(null)}
                onRemoveDraft={removeRatePlanDraft}
                onRetrySchema={refetchRatePlanCreateSchema}
                onToggleDraftExpanded={toggleRatePlanDraftExpanded}
                onUpdateDraft={updateRatePlanDraft}
                ratePlanCreateSchema={ratePlanCreateSchema}
                ratePlanDrafts={ratePlanDrafts}
                readOnly={isRatePlanStepReadOnly}
              />
            )}

            {isRoomStep && (
              <RoomsStep
                amenityOptions={amenityOptions}
                hasRoomDraftFormError={hasRoomDraftFormError}
                isBusy={isStepBusy}
                isLoadingRoomDraftForm={isLoadingRoomDraftForm}
                isLoadingRoomTypesSchema={isLoadingRoomTypesSchema}
                onAddAmenityOption={handleAddAmenityOption}
                onAddRoomDraft={handleAddRoomDraft}
                onRemoveRoomDraft={handleRemoveRoomDraft}
                onRetryLoadRoomFields={() => {
                  setErrorMessage(null);
                  refetchRoomTypesSchema();
                }}
                onUpdateRoomDraft={updateRoomDraft}
                property={selectedProperty}
                roomDrafts={roomDrafts}
                roomTypesFormSchema={roomTypesFormSchema}
                unmatchedSalesforceRoomNames={unmatchedSalesforceRoomNames}
              />
            )}

            {isRoomRatesStep && (
              <RoomRatesStep
                appliedRatePlanNamesByRoomTypeId={appliedRatePlanNamesByRoomTypeId}
                createdRatePlans={createdRatePlans}
                disabled={isStepBusy}
                onClearRooms={() => {
                  if (isRoomRatesStepReadOnly) {
                    return;
                  }

                  setErrorMessage(null);
                  setSelectedRoomRatePlanIds({});
                }}
                onSelectAllRooms={() => {
                  if (isRoomRatesStepReadOnly) {
                    return;
                  }

                  setErrorMessage(null);
                  const defaultRatePlanIds = createdRatePlanIds;
                  setSelectedRoomRatePlanIds(
                    defaultRatePlanIds.length > 0
                      ? Object.fromEntries(
                          selectableRoomRateRoomTypeIds.map((roomTypeId) => [roomTypeId, defaultRatePlanIds]),
                        )
                      : {},
                  );
                }}
                onSelectRatePlans={handleSelectRoomRatePlans}
                readOnly={isRoomRatesStepReadOnly}
                rooms={roomRateRooms}
                selectedRatePlanIds={selectedRoomRatePlanIds}
              />
            )}

            {isCapacityStep && (
              <CapacityStep
                appliedRoomRateIds={capacityAppliedRoomRateIds}
                disabled={isStepBusy}
                failedRoomRateIds={capacityFailedRoomRateIds}
                isApplying={isApplyingDefaultCapacity}
                onApplyDefaults={applyDefaultCapacities}
                roomRates={distinctAppliedRoomRates}
              />
            )}

            {isTaxesAndFeesStep && (
              <TaxesFeesStep
                disabled={isStepBusy}
                formId={TAXES_FEES_FORM_ID}
                onSaved={async () => {
                  setErrorMessage(null);
                  setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
                }}
                onSavingChange={setIsSavingTaxesFees}
                propertyId={selectedProperty?.id}
              />
            )}

            {isPackageSetupStep && isLinkingPackages && <Typography>Linking rooms to packages...</Typography>}

            {isPackageSetupStep && hasAttemptedPackageLink && !isLinkingPackages && matchedRoomRateCount > 0 && (
              <Alert severity="success">
                Matched {matchedRoomRateCount} package{matchedRoomRateCount === 1 ? '' : 's'} to a room and rate.
              </Alert>
            )}

            {isPackageSetupStep && hasAttemptedPackageLink && !isLinkingPackages && packageLinkFailures.length > 0 && (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    disabled={isStepBusy || hasUnresolvedFlexibleNightsWarning}
                    size="small"
                    onClick={() => void packageSetupStepRef.current?.retryLink()}
                  >
                    Retry linking
                  </Button>
                }
              >
                <Typography fontWeight={600}>
                  {matchedRoomRateCount === 0
                    ? 'No packages could be linked to a room and rate. Check the room names match the Salesforce Deal Options, then try again.'
                    : `Saving ${packageLinkFailures.length} package${packageLinkFailures.length === 1 ? '' : 's'} failed:`}
                </Typography>
                <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                  {packageLinkFailures.map((failure) => (
                    <li key={failure}>{failure}</li>
                  ))}
                </Box>
              </Alert>
            )}

            {isPackageSetupStep && hasAttemptedPackageLink && !isLinkingPackages && unmatchedPackages.length > 0 && (
              <UnmatchedPackageRoomPicker
                disabled={isStepBusy}
                onSelectRoomType={(packageId, roomTypeId) =>
                  setManualPackageRoomTypeIds((current) => ({ ...current, [packageId]: roomTypeId }))
                }
                roomTypeOptions={assignableRoomTypes}
                selectedRoomTypeIdByPackageId={manualPackageRoomTypeIds}
                unmatchedPackages={unmatchedPackages}
              />
            )}

            {isPackageSetupStep && !isLinkingPackages && (
              <PackageSetupStep
                // Remount on a changed match set so the edit form re-reads real current values.
                key={matchedPackages.map((pkg) => getPackagePersistId(pkg)).join(',')}
                ref={packageSetupStepRef}
                disabled={isStepBusy}
                formId={PACKAGE_SETUP_FORM_ID}
                offerId={trimmedSalesforceOfferId}
                onRelink={async () => {
                  await handleLinkPackages();
                }}
                onWarningChange={setHasUnresolvedFlexibleNightsWarning}
                onSaved={async () => {
                  // Reaching here already required a clean link attempt, so nothing to relink.
                  setErrorMessage(null);
                  setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
                }}
                onSavingChange={setIsSavingPackageSetup}
                packages={matchedPackages}
              />
            )}

            {isContentStep && contentPageUrl && (
              <Alert severity="info">
                Redirecting to the{' '}
                <Link component="button" onClick={goToContentPage} underline="hover">
                  Offer Builder CM Checklist page
                </Link>
                . If it does not open automatically, use the button below.
              </Alert>
            )}

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <WizardActions
          activeStep={activeStep}
          contentPageUrl={contentPageUrl}
          isContentStep={isContentStep}
          isCreatePropertyStep={isCreatePropertyStep}
          isPackageSetupStep={isPackageSetupStep}
          isPackageSetupStepNextDisabled={isPackageSetupStepNextDisabled}
          isPrerequisitesStepNextDisabled={isPrerequisitesStepNextDisabled}
          isPropertyStepNextDisabled={isPropertyStepNextDisabled}
          isRatePlanStep={isRatePlanStep}
          isRatePlanStepNextDisabled={isRatePlanStepNextDisabled}
          isRoomRatesStepNextDisabled={isRoomRatesStepNextDisabled}
          isRoomStepNextDisabled={isRoomStepNextDisabled}
          isStepBusy={isStepBusy}
          isTaxesAndFeesStep={isTaxesAndFeesStep}
          onGoToContentPage={goToContentPage}
          onNext={handleNext}
          onPrevious={handlePrevious}
          primaryButtonLabel={primaryButtonLabel}
          trimmedSalesforceOfferId={trimmedSalesforceOfferId}
        />
      </Dialog>

      <ExitConfirmationDialog
        onCancel={handleCancelClose}
        onConfirm={handleConfirmClose}
        open={isExitConfirmationOpen}
      />
    </>
  );
}
